// ─── MEMORY GATE ─────────────────────────────────────────────────────────────
// A character's reply becomes memory only when the player answers it.
//
// Nicole, 2026-10-06: "the DM memory is pulling from old scenes again." Every
// surface extracted facts, story events, the locked-facts ledger and the
// rolling summary the instant a reply was saved. One morning Ford re-lived a
// June sushi night as if it were today; Nicole deleted the reply; but
// story_events already held the re-narration, dated today, as the freshest and
// best-ranked memory for the next turn. A hallucination fed itself back in.
//
// DMs got the fix in v408 (DMView.pendingMemoryRef). This is the same rule for
// every other surface: the extractors are STASHED as a closure when a reply
// lands, and RUN when the player sends their next message (or another writer
// answers in a shared room), after checking the reply still exists. A reply
// the player deleted or regenerated is dropped and never happened.
import { supabase } from './supabaseClient.js';

/**
 * @typedef {object} MemoryEntry
 * @property {() => Promise<void> | void} run      the extractors, exactly as the surface called them before
 * @property {string} [table]                       where the reply row lives, for the existence check
 * @property {string} [messageId]                   the reply row id (strongest check)
 * @property {string} [clientTokenBase]             the reply's token family prefix (chunked surfaces)
 * @property {string} [scopeCol]                    the thread column (channel_id, story_id, group_id, conversation_id)
 * @property {string} [scopeId]                     the thread id
 * @property {string} [textCol]                     a text column to match when the row has no id or token (the phone)
 * @property {string} [text]                        the reply text for that match
 */
export function createMemoryGate() {
  /** @type {MemoryEntry|null} */
  let pending = null;

  /** Does the stashed reply still exist? Unknown identifiers count as "yes". */
  async function stillThere(p) {
    try {
      if (p.table && p.messageId) {
        const { data } = await supabase.from(p.table).select('id').eq('id', p.messageId).maybeSingle();
        return !!data;
      }
      if (p.table && p.clientTokenBase && p.scopeCol && p.scopeId) {
        const { data } = await supabase.from(p.table).select('id').eq(p.scopeCol, p.scopeId).like('client_token', `${p.clientTokenBase}%`).limit(1);
        return !!(data && data.length);
      }
      if (p.table && p.textCol && p.text && p.scopeCol && p.scopeId) {
        const { data } = await supabase.from(p.table).select('id').eq(p.scopeCol, p.scopeId).eq(p.textCol, p.text).limit(1);
        return !!(data && data.length);
      }
    } catch (e) {
      console.warn('[memoryGate] existence check failed; keeping the reply:', e?.message || e);
    }
    return true;
  }

  return {
    /** Stash the extractors for the reply that just landed. Replaces any earlier stash (callers flush first). */
    stash(entry) {
      if (!entry || typeof entry.run !== 'function') return;
      pending = entry;
    },
    /** The player rejected a reply: forget the stash if it is that reply. */
    drop({ ids = [], tokenBases = [], texts = [] } = {}) {
      const p = pending;
      if (!p) return false;
      const idHit = p.messageId && ids.map(String).includes(String(p.messageId));
      const tokHit = p.clientTokenBase && tokenBases.some(t => t && String(t).startsWith(String(p.clientTokenBase)));
      const textHit = p.text && texts.some(t => t && String(t).trim() === String(p.text).trim());
      if (idHit || tokHit || textHit) { pending = null; return true; }
      return false;
    },
    /** The player answered (or moved on): write the stashed reply into memory if it still exists. */
    async flush() {
      const p = pending;
      if (!p) return false;
      pending = null;
      if (!(await stillThere(p))) return false;
      try { await p.run(); } catch (e) { console.warn('[memoryGate] deferred memory failed:', e?.message || e); }
      return true;
    },
    /** For tests and diagnostics. */
    peek() { return pending; },
  };
}
